import { useMemo } from "react";
import type { PendingConversation } from "../../../types/pending-conversation";
import { LoadingButton } from "../../button/loading-button";
import { THEME } from "../../../styles/theme";

const getTimestampMs = (timestamp?: string) => {
  if (!timestamp) return Number.NEGATIVE_INFINITY;

  const parsed = new Date(timestamp).getTime();
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

interface ConversationSidebarProps {
  abaAtiva: "em_espera" | "em_atendimento";
  setAbaAtiva: (v: "em_espera" | "em_atendimento") => void;

  conversations: PendingConversation[];
  pendingConversations: PendingConversation[];

  onSelect: (conversation: PendingConversation) => void;
  supervisionMode?: boolean;
  canSupervise?: boolean;
  onToggleSupervision?: () => void;
  supervisionLoading?: boolean;
}

export function ConversationSidebar({
  abaAtiva,
  setAbaAtiva,
  conversations,
  pendingConversations,
  onSelect,
  supervisionMode = false,
  canSupervise = false,
  onToggleSupervision,
  supervisionLoading = false,
}: ConversationSidebarProps) {
  const list = supervisionMode
    ? conversations
    : abaAtiva === "em_espera"
      ? pendingConversations
      : conversations;

  const sortedList = useMemo(() => {
    return [...list].sort((a, b) => {
      const timeA = getTimestampMs(a.timestamp);
      const timeB = getTimestampMs(b.timestamp);

      return timeB - timeA;
    });
  }, [list]);

  return (
    <div className="flex min-h-0 max-w-full flex-col border-r border-slate-700 bg-blue-950 p-4 text-white">
      <div className="flex items-center gap-2 mb-4 justify-between">
        <h2 className="text-lg font-semibold text-white">Atendimentos</h2>

        {canSupervise && (
          <LoadingButton
            onClick={onToggleSupervision}
            className={`btn cursor-pointer px-3 py-1 text-xs ${
              supervisionMode
                ? "bg-white text-slate-900 hover:bg-slate-100"
                : "bg-emerald-400 text-slate-900 hover:bg-emerald-300"
            }`}
            isLoading={supervisionLoading}
            loadingLabel="Carregando"
          >
            {supervisionMode ? "Sair da Supervisão" : "Modo Supervisão"}
          </LoadingButton>
        )}
      </div>

      {!supervisionMode && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setAbaAtiva("em_atendimento")}
            className={`cursor-pointer flex flex-1 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition ${
              abaAtiva === "em_atendimento"
                ? "bg-slate-700 text-white"
                : "bg-slate-800 text-slate-100 hover:bg-slate-600"
            }`}
          >
            Em Atendimento
          </button>

          <button
            onClick={() => setAbaAtiva("em_espera")}
            className={`cursor-pointer flex flex-1 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition ${
              abaAtiva === "em_espera"
                ? "bg-slate-700 text-white"
                : "bg-slate-800 text-slate-100 hover:bg-slate-600"
            }`}
          >
            <span className="mr-2">Em espera</span>

            <span
              className={`status-dot ${
                pendingConversations.length > 0
                  ? "status-dot-danger"
                  : "bg-transparent"
              }`}
            />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-2">
        {sortedList.map((c) => {
          const inicial = c?.userName?.charAt(0).toUpperCase();
          const avatarColor = c.userColor ?? THEME.colors.avatarFallback;

          return (
            <div
              key={c.conversationId}
              onClick={() => {
                if (!c.awaitingConsentResponse) onSelect(c);
              }}
              aria-disabled={c.awaitingConsentResponse || undefined}
              title={
                c.awaitingConsentResponse
                  ? "Aguardando o contato responder ao consentimento enviado"
                  : undefined
              }
              className={`flex items-center gap-3 rounded-lg p-3 transition ${
                c.awaitingConsentResponse
                  ? "cursor-not-allowed bg-slate-800/60"
                  : "cursor-pointer hover:bg-slate-700/70"
              }`}
            >
              <div
                className="w-8 h-8 text-slate-800 rounded-full flex items-center justify-center font-bold"
                style={{ backgroundColor: avatarColor }}
              >
                {inicial}
              </div>
              <div className="text-sm font-medium flex flex-col gap-1">
                <span>{c?.userName}</span>
                {c.awaitingConsentResponse && (
                  <span className="w-fit rounded-full border border-amber-300/50 bg-amber-300/15 px-2 py-0.5 text-[11px] font-semibold text-amber-200">
                    Aguardando resposta
                  </span>
                )}
                {supervisionMode &&
                  (c?.attendantName || c.status === "AI_ATTENDANCE") && (
                    <span className="text-xs text-slate-300">
                      Atendente: {c.attendantName ?? "IA"}
                    </span>
                  )}
              </div>
              {c.hasUnread && abaAtiva === "em_atendimento" && (
                <span className="status-dot status-dot-danger ml-auto h-3 w-3" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
